import axios from "axios";
import React from "react";

import { useMsal } from "@azure/msal-react";
import {
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { useMutation } from "@tanstack/react-query";

import {
  Loader,
  PyButton,
  TOASTER_TYPE,
  ToastMessage,
} from "../../../../../components";
import { useUser } from "../../../../../context";
import { createCartToken } from "../../../../../services/api";
import { TCredential } from "../../../PaymentMethod";

interface IProps {
  credentials: TCredential;
  refetchCardToken: () => void;
  openMethodAccordion: () => void;
  cardTokenLoading: boolean;
  setupIntentData: any;
  stripeCustomer: any;
}

export const AddStripePaymentMethod: React.FC<IProps> = ({
  credentials,
  refetchCardToken,
  openMethodAccordion,
  cardTokenLoading,
  setupIntentData,
  stripeCustomer,
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const { accounts } = useMsal();
  const { userDetails } = useUser();

  const onCreateCartToken = useMutation(createCartToken, {
    onSuccess: (res: any) => {
      if (res.status === 200 || res.status === 201) {
        openMethodAccordion();
        refetchCardToken();
        ToastMessage(TOASTER_TYPE.SUCCESS, "Card added successfully");
      }
    },
    onError: (error: unknown) => {
      if (axios.isAxiosError(error)) {
        ToastMessage(
          TOASTER_TYPE.ERROR,
          error.response?.data?.message || "Failed to save card"
        );
      }
    },
    retry: 3,
  });

  const handleSubmit = async () => {
    if (!stripe || !elements) {
      ToastMessage(TOASTER_TYPE.ERROR, "Stripe not initialized");
      return;
    }

    if (!stripeCustomer?.id) {
      ToastMessage(TOASTER_TYPE.ERROR, "Stripe customer not available");
      return;
    }

    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      redirect: "if_required",
      confirmParams: {
        return_url: window.location.href,
      },
    });

    if (error) {
      ToastMessage(TOASTER_TYPE.ERROR, error.message || "Payment failed");
      console.error(error);
      return;
    }

    if (setupIntent && setupIntent.status === "succeeded") {
      ToastMessage(TOASTER_TYPE.SUCCESS, "Card verified successfully");
      console.log("setupIntent", setupIntent);
      handleCreateCardToken(setupIntent, stripeCustomer.id);
    }
  };

  const handleCreateCardToken = (setupIntent: any, customerId: string) => {
    const body = {
      merchantUniqueReferenceNumber: credentials.customerUniqueReferenceNumber,
      customerUniqueReferenceNumber: credentials.customerUniqueReferenceNumber,
      paymentGatewayReference:
        setupIntent?.payment_method || setupIntent?.paymentMethodId,
      customerEmailAddress: credentials.email,
      walletUniqueReferenceNumber: accounts[0]?.idTokenClaims?.sub,
      consumerUniqueReferenceNumber: credentials.consumerUniqueReferenceNumber,
      newCustomer: false,
      gatewayCustomerId: customerId,
      verification: {
        verificationRequired: false,
        firstName: userDetails?.firstName as string,
        lastName: userDetails?.lastName as string,
        cardType: "debit",
        brand: "unknown",
      },
    };

    console.log(body);

    onCreateCartToken.mutate(body as any);
  };

  return (
    <>
      {(onCreateCartToken.isLoading || cardTokenLoading) && (
        <Loader isFullPage />
      )}

      <div className="flex flex-col gap-4 w-full rounded-2xl">
        <div className="w-full">
          <PaymentElement
            options={{
              layout: "tabs",
            }}
          />
        </div>

        <div className="flex gap-4 items-center mt-4">
          <PyButton variant="SecondaryOutline" extraClass="!w-[104px]">
            Cancel
          </PyButton>
          <PyButton
            click={handleSubmit}
            type="button"
            extraClass="!w-[104px]"
            isDisabled={!stripe || !elements || !stripeCustomer?.id}
            loading={onCreateCartToken.isLoading}>
            Add
          </PyButton>
        </div>
      </div>
    </>
  );
};
